import type React from 'react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';
import { MarsinetContext } from 'apps/marsinet/src/app/context/marsinetProvider';
import { useToast } from 'apps/marsinet/src/app/context/ToastProvider';
import { apiServices } from 'apps/marsinet/src/app/services/apiServices';
import { CreateMaintenanceOrderDto } from 'apps/marsinet/src/app/typescript-axios';
import { normalizeApiError } from 'apps/marsinet/src/app/errors/normalize-api-error';
import {
  getLocalDateTimeInTimeZone,
  localDateTimeToUtc,
} from 'apps/marsinet/src/app/utils/timezone-utils';

/* -------------------------------------------------------------------------- */
/* Types                                                                       */
/* -------------------------------------------------------------------------- */

type DropdownOption = {
  label: string;
  value: string;
};

type DeviceOption = DropdownOption & {
  serial?: string;
};

/** One entry of any RPS catalog returned by getAllCreateFormOptions. */
type RpsOption = {
  id: string;
  code: string;
  description: string;
};

type MaintenanceOrderFormOptions = {
  type: RpsOption[];
  status: RpsOption[];
};

// Tipos de restricción según el origen de creación de la OM
type MaintenanceTypeRestriction = 'corrective' | 'preventive-scheduled';

// TODO: sustituir por los uuid reales de RPS para cada tipo de OM
export const MAINTENANCE_TYPE_IDS = {
  CORRECTIVE: '88b7499c-04fb-4fc8-b728-6bad3aa4cdc9MB2', // id de RPS para "Correctiva"
  PREVENTIVE: '88c42cca-dd10-44c7-bf76-513fac1b556bMB2', // id de RPS para "Preventiva"
  SCHEDULED: 'c88d4846-ce22-4e3a-9f3e-f2d3f4fd5fb7MB2', // id de RPS para "Programada"
};

interface CreateMaintenanceOrderModalProps {
  visible: boolean;
  setVisible: (visible: boolean) => void;

  initialDeviceSerial?: string | null;
  lockDevice?: boolean;

  // NUEVO
  maintenanceTypeRestriction?: MaintenanceTypeRestriction;

  maintenanceOrderFunction: (values: CreateMaintenanceOrderDto, deviceId?: string) => Promise<any>;
  onAddPendingMaintenanceOrder?: (values: CreateMaintenanceOrderDto) => void;
}

/* -------------------------------------------------------------------------- */
/* Constants                                                                   */
/* -------------------------------------------------------------------------- */

const ATLAS_MODEL = 5;

const EMPTY_FORM_OPTIONS: MaintenanceOrderFormOptions = {
  type: [],
  status: [],
};

/**
 * RPS code of the status the form starts on. If no option matches, the field
 * is left empty and the user has to pick one.
 */
const DEFAULT_STATUS_CODE = 'PEND';

const API_FIELD_TO_FORM_FIELD: Record<string, string> = {
  serial_number: 'd_id',
  serialNumber: 'd_id',
  description: 'description',
  maintenance_type: 'maintenance_type',
  status: 'status',
  responsible: 'responsible',
  creation_date: 'creation_date',
  entry_date: 'entry_date',
  observations: 'observations',
  evidences: 'evidences',
};

/** RPS catalogs carry the uuid in `id`; that is what the DTO expects. */
const toDropdownOptions = (options: RpsOption[]): DropdownOption[] =>
  options
    .filter((option) => option?.id && option?.description)
    .map((option) => ({ label: option.description, value: option.id }));

/* -------------------------------------------------------------------------- */
/* Component                                                                   */
/* -------------------------------------------------------------------------- */

const ModalCreateMaintenanceOrder: React.FC<
  CreateMaintenanceOrderModalProps
> = ({
  visible,
  setVisible,
  initialDeviceSerial,
  lockDevice = false,
  maintenanceOrderFunction,
  onAddPendingMaintenanceOrder,
  maintenanceTypeRestriction,
}) => {
    const { t } = useTranslation();
    const { access_token, selectedTimezone } = useContext(MarsinetContext);
    const toast = useToast();

    const isPendingMode = Boolean(onAddPendingMaintenanceOrder);

    const [devices, setDevices] = useState<DeviceOption[]>([]);
    const [responsibleOptions, setResponsibleOptions] = useState<
      DropdownOption[]
    >([]);
    const [defaultResponsible, setDefaultResponsible] = useState('');
    const [isLoadingResponsibles, setIsLoadingResponsibles] = useState(false);
    const [rpsOptions, setRpsOptions] =
      useState<MaintenanceOrderFormOptions>(EMPTY_FORM_OPTIONS);
    const [isLoadingRpsOptions, setIsLoadingRpsOptions] = useState(false);
    const [rpsOptionsError, setRpsOptionsError] = useState(false);
    /* ---------------------------------------------------------------------- */
    /* Catalogs                                                                */
    /* ---------------------------------------------------------------------- */

    const loadDevices = useCallback(async () => {
      try {
        const { data } =
          await apiServices.myAccountApi.myAccountControllerGetMyDevices();

        setDevices(
          (data?.devices ?? []).map((device) => ({
            label: `${device.model === ATLAS_MODEL ? 'AT' : 'EXP'}-${device.serial}`,
            value: device.d_id,
            serial: String(device.serial ?? '').trim() || undefined,
          }))
        );
      } catch {
        setDevices([]);
      }
    }, []);

    /**
     * Tipo y estado vienen de RPS: el DTO espera los uuid, no las constantes
     * que estaban hardcodeadas.
     */
    const loadRpsOptions = useCallback(async () => {
      setIsLoadingRpsOptions(true);
      setRpsOptionsError(false);
      try {
        const { data } =
          await apiServices.maintenanceOrderApi.maintenanceOrdersControllerGetAllCreateFormOptions();

        setRpsOptions({
          type: data?.type ?? [],
          status: data?.status ?? [],
        });
      } catch {
        setRpsOptions(EMPTY_FORM_OPTIONS);
        setRpsOptionsError(true);
      } finally {
        setIsLoadingRpsOptions(false);
      }
    }, []);

    /**
     * Same catalog the incidence modal uses for "detectado por": the RPS user
     * list plus the default that should be pre-selected.
     */
    const loadResponsibles = useCallback(async () => {
      setIsLoadingResponsibles(true);
      try {
        const { data } =
          await apiServices.userApi.usersControllerGetIncidentDetectedByOptions();

        const isMarsiOrganization = data?.is_marsi_organization ?? false;
        const organizationName = data?.organization_name ?? '';
        const defaultDetectedBy = data?.default_detected_by ?? '';
        const currentUserEmail = data?.current_user_email ?? '';

        const options: DropdownOption[] = (data?.options ?? []).map((option) => ({
          label: option.email,
          value: option.email,
        }));

        const defaultValue = isMarsiOrganization
          ? currentUserEmail ||
          (defaultDetectedBy.includes('@') ? defaultDetectedBy : '')
          : defaultDetectedBy;

        if (defaultValue && !options.some((o) => o.value === defaultValue)) {
          options.unshift({
            label: isMarsiOrganization
              ? defaultValue
              : organizationName || defaultValue,
            value: defaultValue,
          });
        }

        setResponsibleOptions(options);
        setDefaultResponsible(defaultValue);
      } catch {
        setResponsibleOptions([]);
        setDefaultResponsible('');
      } finally {
        setIsLoadingResponsibles(false);
      }
    }, []);

    useEffect(() => {
      if (visible && access_token) {
        loadDevices();
        loadRpsOptions();
        loadResponsibles();
      }
    }, [visible, access_token, loadDevices, loadRpsOptions, loadResponsibles]);

    // const maintenanceTypeOptions = useMemo(
    //   () => toDropdownOptions(rpsOptions.type),
    //   [rpsOptions.type],
    // );
    const allowedTypeIds = useMemo(() => {
      if (!maintenanceTypeRestriction) return null;
      return maintenanceTypeRestriction === 'corrective'
        ? [MAINTENANCE_TYPE_IDS.CORRECTIVE]
        : [MAINTENANCE_TYPE_IDS.PREVENTIVE, MAINTENANCE_TYPE_IDS.SCHEDULED];
    }, [maintenanceTypeRestriction]);

    const maintenanceTypeOptions = useMemo(() => {
      const all = toDropdownOptions(rpsOptions.type);
      if (!allowedTypeIds) return all;

      return all.filter((option) => allowedTypeIds.includes(option.value));
    }, [rpsOptions.type, allowedTypeIds]);

    const statusOptions = useMemo(
      () => toDropdownOptions(rpsOptions.status),
      [rpsOptions.status]
    );

    const catalogsDisabled = isLoadingRpsOptions || rpsOptionsError;

    /* ---------------------------------------------------------------------- */
    /* Validation                                                              */
    /* ---------------------------------------------------------------------- */

    const validationSchema = useMemo(
      () =>
        Yup.object({
          d_id: lockDevice
            ? Yup.string()
            : Yup.string().required(t('validations.required')),
          maintenance_type: Yup.string().required(t('validations.required')),
          status: Yup.string().required(t('validations.required')),
          responsible: Yup.string().required(t('validations.required')),
          description: Yup.string().max(4000),
          creation_date: Yup.date()
            .nullable()
            .required(t('validations.required')),
          entry_date: Yup.date().nullable().required(t('validations.required')),
          observations: Yup.string().max(4000),
          evidences: Yup.string().max(4000),
        }),
      [t, lockDevice]
    );

    /* ---------------------------------------------------------------------- */
    /* Initial values                                                          */
    /* ---------------------------------------------------------------------- */

    const initialDeviceId = useMemo(() => {
      if (!initialDeviceSerial) return '';
      const normalized = String(initialDeviceSerial).trim();
      return devices.find((device) => device.serial === normalized)?.value ?? '';
    }, [devices, initialDeviceSerial]);

    const defaultStatusId = useMemo(
      () =>
        rpsOptions.status.find(
          (option) => option.code?.trim().toUpperCase() === DEFAULT_STATUS_CODE
        )?.id ?? '',
      [rpsOptions.status]
    );

    const lockMaintenanceType = maintenanceTypeRestriction === 'corrective';

    const defaultTypeId = useMemo(() => {
      if (!lockMaintenanceType) return '';
      return maintenanceTypeOptions[0]?.value ?? '';
    }, [lockMaintenanceType, maintenanceTypeOptions]);

    const initialValues = useMemo(
      () => ({
        d_id: initialDeviceId,
        description: '',
        maintenance_type: defaultTypeId,
        status: defaultStatusId,
        responsible: defaultResponsible,
        // El selector usa la hora local de la zona seleccionada hasta el envío.
        creation_date: getLocalDateTimeInTimeZone(new Date(), selectedTimezone),
        entry_date: getLocalDateTimeInTimeZone(new Date(), selectedTimezone),
        observations: '',
        evidences: '',
      }),
      [
        initialDeviceId,
        defaultTypeId,
        defaultStatusId,
        defaultResponsible,
        selectedTimezone,
      ]
    );

    /* ---------------------------------------------------------------------- */
    /* Submit                                                                  */
    /* ---------------------------------------------------------------------- */

    const formik = useFormik({
      initialValues,
      validationSchema,
      enableReinitialize: true,
      onSubmit: async (values, { setFieldError, resetForm }) => {
        const selectedDevice = devices.find(
          (device) => device.value === values.d_id
        );

        // Exactly the fields CreateMaintenanceOrderDto accepts.
        // maintenance_type y status son uuid de RPS.
        // Convierte las horas introducidas a UTC antes de enviarlas al backend.
        const payload: CreateMaintenanceOrderDto = {
          serial_number: selectedDevice?.serial || undefined,
          description: values.description.trim() || undefined,
          maintenance_type: values.maintenance_type,
          status: values.status,
          responsible: values.responsible || undefined,
          creation_date: values.creation_date
            ? new Date(
              localDateTimeToUtc(values.creation_date, selectedTimezone)
            )
            : undefined,
          entry_date: values.entry_date
            ? new Date(
              localDateTimeToUtc(values.entry_date, selectedTimezone)
            )
            : undefined,
          observations: values.observations.trim() || undefined,
          evidences: values.evidences.trim() || undefined,
        };

        const draft = {
          ...payload,
          component: selectedDevice?.label ?? selectedDevice?.serial ?? '',
          device_serial: selectedDevice?.serial ?? null,
          order_status: values.status,
        };

        try {
          if (onAddPendingMaintenanceOrder) {
            onAddPendingMaintenanceOrder(draft);
          } else {
            await maintenanceOrderFunction(payload, values.d_id || undefined);
          }

          resetForm();
          setVisible(false);
        } catch (error: any) {
          const responseData = error?.response?.data;

          // Keep the dialog open so the user does not lose what they typed.
          if (error?.response?.status === 400 && responseData?.missingFields) {
            responseData.missingFields.forEach((field: string) => {
              setFieldError(
                API_FIELD_TO_FORM_FIELD[field] ?? field,
                t('validations.required')
              );
            });
            return;
          }

          const normalizedError = normalizeApiError(error);

          toast.showToast(
            'error',
            t('messages.error'),
            t(normalizedError.messageKey, normalizedError.params)
          );
        }
      },
    });

    /* Clear a selection that no longer exists after reloading the catalogs. */
    const { maintenance_type: selectedType, status: selectedStatus } =
      formik.values;
    const { setFieldValue } = formik;

    useEffect(() => {
      if (
        selectedType &&
        maintenanceTypeOptions.length > 0 &&
        !maintenanceTypeOptions.some((o) => o.value === selectedType)
      ) {
        setFieldValue('maintenance_type', '');
      }
    }, [maintenanceTypeOptions, selectedType, setFieldValue]);

    useEffect(() => {
      if (
        selectedStatus &&
        statusOptions.length > 0 &&
        !statusOptions.some((o) => o.value === selectedStatus)
      ) {
        setFieldValue('status', '');
      }
    }, [statusOptions, selectedStatus, setFieldValue]);

    /* ---------------------------------------------------------------------- */
    /* Dialog helpers                                                          */
    /* ---------------------------------------------------------------------- */

    const onPageHide = () => {
      formik.resetForm();
      setVisible(false);
    };

    const isFieldInvalid = (name) =>
      Boolean(formik.touched[name] && formik.errors[name]);

    const fieldError = (name) =>
      isFieldInvalid(name) ? String(formik.errors[name]) : null;

    const requiredMark = (
      <span className="text-red-500 ml-1" aria-hidden="true">
        *
      </span>
    );

    /* ---------------------------------------------------------------------- */
    /* Render                                                                  */
    /* ---------------------------------------------------------------------- */

    return (
      <Dialog
        visible={visible}
        onHide={onPageHide}
        header={
          <div>
            <h2>{t('pages.incidents.createOrder')}</h2>
          </div>
        }
        style={{ width: '1360px', minHeight: '60vh', maxHeight: '90vh' }}
        breakpoints={{ '960px': '75vw', '641px': '100vw' }}
      >
        <form autoComplete="off" onSubmit={formik.handleSubmit}>
          <div className="grid formgrid p-fluid">
            {/* Dispositivo -> serial_number */}
            <div className="field col-12 md:col-6">
              <label htmlFor="maintenance-order-device" className="font-bold">
                {t('pages.incidents.device_name')}
                {requiredMark}
              </label>
              <Dropdown
                id="maintenance-order-device"
                name="d_id"
                value={formik.values.d_id}
                options={devices}
                optionLabel="label"
                optionValue="value"
                placeholder="Seleccionar dispositivo"
                emptyMessage="No hay dispositivos disponibles"
                emptyFilterMessage="No se ha encontrado ningún dispositivo"
                disabled={lockDevice}
                showClear={!lockDevice}
                filter={!lockDevice}
                filterBy="label"
                filterMatchMode="contains"
                filterPlaceholder={t('pages.maintenance_orders.labelDescription')}
                onBlur={formik.handleBlur}
                onChange={(e) => formik.setFieldValue('d_id', e.value ?? '')}
                className={`w-full ${isFieldInvalid('d_id') ? 'p-invalid' : ''}`}
              />

              {/* El equipo de la incidencia puede no estar dado de alta en
                  Marsinet: el dropdown se queda vacío pero la OM se crea
                  igualmente sobre el componente correcto. */}
              {lockDevice && !formik.values.d_id && initialDeviceSerial && (
                <small className="text-600">{initialDeviceSerial}</small>
              )}

              {fieldError('d_id') && (
                <small className="p-error">{fieldError('d_id')}</small>
              )}
            </div>

            {/* Descripción */}
            <div className="field col-12 md:col-6">
              <label
                htmlFor="maintenance-order-description"
                className="font-bold"
              >
                {t('pages.maintenance_orders.description')}
              </label>
              <InputTextarea
                id="maintenance-order-description"
                name="description"
                value={formik.values.description}
                placeholder={t(
                  'pages.incidents.createMaintenanceOrder.description_placeholder'
                )}
                maxLength={4000}
                rows={1}
                autoResize
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                className={isFieldInvalid('description') ? 'p-invalid' : ''}
                style={{ minHeight: '42px', resize: 'none' }}
              />
              {fieldError('description') && (
                <small className="p-error">{fieldError('description')}</small>
              )}
            </div>

            {/* Tipo de mantenimiento -> uuid RPS */}
            <div className="field col-12 md:col-6">
              <label
                htmlFor="maintenance-order-maintenance-type"
                className="font-bold"
              >
                {t(
                  'pages.incidents.createMaintenanceOrder.maintenance_type_label'
                )}
                {requiredMark}
              </label>
              <Dropdown
                id="maintenance-order-maintenance-type"
                name="maintenance_type"
                value={formik.values.maintenance_type}
                options={maintenanceTypeOptions}
                placeholder={
                  isLoadingRpsOptions
                    ? 'Cargando tipo...'
                    : t(
                      'pages.incidents.createMaintenanceOrder.maintenance_type_placeholder'
                    )
                }
                emptyMessage="No hay tipos disponibles"
                disabled={catalogsDisabled || lockMaintenanceType}
                showClear={!lockMaintenanceType}
                filter={!lockMaintenanceType}
                filterBy="label"
                filterMatchMode="contains"
                filterPlaceholder={t('pages.maintenance_orders.labelDescription')}
                onBlur={formik.handleBlur}
                onChange={(e) =>
                  formik.setFieldValue('maintenance_type', e.value)
                }
                className={isFieldInvalid('maintenance_type') ? 'p-invalid' : ''}
              />
              {fieldError('maintenance_type') && (
                <small className="p-error">
                  {fieldError('maintenance_type')}
                </small>
              )}
            </div>

            {/* Estado -> uuid RPS */}
            <div className="field col-12 md:col-6">
              <label htmlFor="maintenance-order-status" className="font-bold">
                {t('pages.incidents.createMaintenanceOrder.order_status_label')}
                {requiredMark}
              </label>
              <Dropdown
                id="maintenance-order-status"
                name="status"
                value={formik.values.status}
                options={statusOptions}
                placeholder={
                  isLoadingRpsOptions
                    ? 'Cargando estado...'
                    : t(
                      'pages.incidents.createMaintenanceOrder.order_status_placeholder'
                    )
                }
                emptyMessage="No hay estados disponibles"
                disabled={catalogsDisabled}
                filter
                filterBy="label"
                filterMatchMode="contains"
                onBlur={formik.handleBlur}
                onChange={(e) => formik.setFieldValue('status', e.value)}
                className={isFieldInvalid('status') ? 'p-invalid' : ''}
              />
              {fieldError('status') && (
                <small className="p-error">{fieldError('status')}</small>
              )}
            </div>

            {/* Responsable — mismo catálogo que "detectado por" en incidencias */}
            <div className="field col-12 md:col-6">
              <label
                htmlFor="maintenance-order-responsible"
                className="font-bold"
              >
                {t('pages.maintenance_orders.responsible')}
                {requiredMark}
              </label>
              <Dropdown
                id="maintenance-order-responsible"
                name="responsible"
                value={formik.values.responsible}
                options={responsibleOptions}
                placeholder={
                  isLoadingResponsibles
                    ? 'Cargando responsables...'
                    : 'Selecciona responsable'
                }
                disabled={isLoadingResponsibles}
                filter
                filterBy="label"
                filterMatchMode="contains"
                onBlur={formik.handleBlur}
                onChange={(e) => formik.setFieldValue('responsible', e.value)}
                className={isFieldInvalid('responsible') ? 'p-invalid' : ''}
              />
              {fieldError('responsible') && (
                <small className="p-error">{fieldError('responsible')}</small>
              )}
            </div>

            {/* Fecha de creación -> creation_date */}
            <div className="field col-12 md:col-6">
              <label
                htmlFor="maintenance-order-creation-date"
                className="font-bold"
              >
                {t('pages.maintenance_orders.creation_date')}
                {requiredMark}
              </label>
              <Calendar
                id="maintenance-order-creation-date"
                name="creation_date"
                value={formik.values.creation_date}
                placeholder="dd/MM/yyyy HH:mm"
                dateFormat="dd/mm/yy"
                hourFormat="24"
                showIcon
                showTime
                readOnlyInput
                onBlur={formik.handleBlur}
                onChange={(e) =>
                  formik.setFieldValue('creation_date', e.value ?? null)
                }
                className={`w-full ${isFieldInvalid('creation_date') ? 'p-invalid' : ''}`}
              />
              {fieldError('creation_date') && (
                <small className="p-error">{fieldError('creation_date')}</small>
              )}
            </div>

            {/* Fecha de ejecución -> entry_date */}
            <div className="field col-12 md:col-6">
              <label htmlFor="maintenance-order-entry-date" className="font-bold">
                {t('pages.maintenance_orders.execution_date')}
                {requiredMark}
              </label>
              <Calendar
                id="maintenance-order-entry-date"
                name="entry_date"
                value={formik.values.entry_date}
                placeholder="dd/MM/yyyy HH:mm"
                dateFormat="dd/mm/yy"
                hourFormat="24"
                showIcon
                showTime
                readOnlyInput
                onBlur={formik.handleBlur}
                onChange={(e) =>
                  formik.setFieldValue('entry_date', e.value ?? null)
                }
                className={`w-full ${isFieldInvalid('entry_date') ? 'p-invalid' : ''}`}
              />
              {fieldError('entry_date') && (
                <small className="p-error">{fieldError('entry_date')}</small>
              )}
            </div>

            {/* Observaciones -> el backend las anexa a la descripción */}
            <div className="field col-12 md:col-6">
              <label
                htmlFor="maintenance-order-observations"
                className="font-bold"
              >
                {t('pages.maintenance_orders.observations')}
              </label>
              <InputTextarea
                id="maintenance-order-observations"
                name="observations"
                value={formik.values.observations}
                rows={4}
                autoResize
                maxLength={4000}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                className={`w-full ${isFieldInvalid('observations') ? 'p-invalid' : ''}`}
              />
              {fieldError('observations') && (
                <small className="p-error">{fieldError('observations')}</small>
              )}
            </div>

            {/* Evidencias -> el backend las anexa a la descripción */}
            <div className="field col-12 md:col-6">
              <label htmlFor="maintenance-order-evidences" className="font-bold">
                {t('pages.maintenance_orders.evidences')}
              </label>
              <InputTextarea
                id="maintenance-order-evidences"
                name="evidences"
                value={formik.values.evidences}
                rows={4}
                autoResize
                maxLength={4000}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                className={`w-full ${isFieldInvalid('evidences') ? 'p-invalid' : ''}`}
              />
              {fieldError('evidences') && (
                <small className="p-error">{fieldError('evidences')}</small>
              )}
            </div>
          </div>

          <div className="border-top-1 surface-border mt-3 mb-3" />

          <div className="flex align-items-center justify-content-end">
            <Button
              id="button-create-maintenance-order"
              severity="secondary"
              icon="pi pi-save"
              label={
                isPendingMode
                  ? t('pages.incidents.createMaintenanceOrder.button_add_om')
                  : t('save')
              }
              className="p-button-raised"
              type="submit"
              loading={formik.isSubmitting}
              disabled={formik.isSubmitting || catalogsDisabled}
            />
          </div>
        </form>
      </Dialog>
    );
  };

export default ModalCreateMaintenanceOrder;
